<template>
  <FullScreenLoader v-if="authStore.bankDetailsLoading || authStore.profileLoading" />

  <!-- ── Banner de progresso do onboarding ────────────── -->
  <div v-if="isOnboarding" class="onboarding-header">
    <div class="onboarding-header__progress">
      <div class="onboarding-header__step onboarding-header__step--done">
        <i class="fas fa-check"></i> Conta criada
      </div>
      <div class="onboarding-header__arrow">→</div>
      <div class="onboarding-header__step onboarding-header__step--active">
        <i class="fas fa-user"></i> Seus dados
      </div>
      <div class="onboarding-header__arrow">→</div>
      <div class="onboarding-header__step">
        <i class="fas fa-gift"></i> Criar lista
      </div>
    </div>
    <p class="onboarding-header__hint">
      <i class="fas fa-lock me-1 text-muted"></i>
      Seus dados estão seguros. Falta só este passo para concluir sua conta.
    </p>
  </div>

  <div :class="['create-event-page container-fluid p-0', { 'modal-active': showConfirmationModal || showErrorModal }]">

    <!-- ── Step navigation ──────────────────────────────────────── -->
    <nav class="steps-navigation">
      <div class="progress-track">
        <div
          class="progress-fill"
          :style="{ width: progressWidth }"
        ></div>
      </div>

      <div class="steps-row">
        <div
          v-for="step in steps"
          :key="step.id"
          :class="['step-dot-item', {
            active: currentStep === step.id,
            completed: currentStep > step.id,
          }]"
          @click="goToStep(step.id)"
          role="button"
          tabindex="0"
        >
          <div class="step-dot">
            <i v-if="currentStep > step.id" class="fas fa-check"></i>
            <span v-else>{{ step.id }}</span>
          </div>
          <span class="step-dot-label">{{ step.name }}</span>
        </div>
      </div>
    </nav>

    <!-- ── Step content ─────────────────────────────────────────── -->
    <main class="step-content-wrapper">
      <div class="container-fluid" v-if="isProfileReady">
        <!-- Avatar section — apenas na edição de perfil comum -->
        <div v-if="!isStripeFlow" class="avatar-section">
          <div class="avatar-preview" @click="triggerAvatarInput">
            <img v-if="avatarPreview" :src="avatarPreview" class="avatar-preview__img" alt="Avatar" referrerpolicy="no-referrer" />
            <div v-else class="avatar-preview__placeholder">
              <span class="avatar-preview__initial">{{ userInitial }}</span>
            </div>
            <div class="avatar-preview__overlay"><i class="fas fa-camera"></i></div>
          </div>
          <div class="avatar-section__info">
            <p class="avatar-section__name">{{ authStore.user?.name }}</p>
            <button type="button" class="avatar-section__btn" @click="triggerAvatarInput" :disabled="avatarUploading">
              <span v-if="avatarUploading" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="fas fa-pen me-1"></i>
              {{ avatarUploading ? 'Salvando...' : 'Alterar foto' }}
            </button>
          </div>
          <input ref="avatarInputRef" type="file" accept="image/*" class="d-none" @change="onAvatarFileSelected" />
        </div>

        <ImageCropperModal
          :visible="avatarCropperVisible"
          :src="avatarCropperSrc"
          :aspect-ratio="1"
          title="Ajuste sua foto de perfil"
          subtitle="Posicione e enquadre como preferir."
          @confirm="onAvatarCropConfirm"
          @cancel="avatarCropperVisible = false"
        />

        <div v-show="currentStep === 1">
          <Step1PersonalData
            ref="step1"
            :use-register-data="isCreateAccountFlow"
            :gateway-errors="gatewayFieldErrors"
            @clear-gateway-error="clearGatewayError"
          />
        </div>

        <div v-show="currentStep === 2" class="step-content py-4 p-0 pt-1 p-md-4">
          <div class="payment-stage-header mb-3">
            <div class="payment-stage-header__top">
              <h3 class="step-title mb-1">
                {{ paymentStepTitle }}
                <span v-if="canGoDashboardFromPaymentStep" class="title-verified-seal" role="status" aria-label="Conta verificada">
                  <i class="fas fa-check"></i>
                </span>
              </h3>

              <span class="payment-stage-security">
                <i class="fas fa-lock me-1"></i> Verificacao segura
              </span>
            </div>

            <p class="step-description mb-2">
              {{ paymentStepDescription }}
            </p>

            <div class="payment-stage-benefits payment-stage-benefits--compact">
              <span><i class="fas fa-check-circle me-1"></i> Transparencia de recebimento</span>
              <span><i class="fas fa-check-circle me-1"></i> Verificacao segura e automatica</span>
              <span><i class="fas fa-check-circle me-1"></i> Dados protegidos</span>
            </div>

          </div>

          <p v-if="paymentStepError" class="alert alert-danger mb-3">
            {{ paymentStepError }}
          </p>

          <div v-if="canGoDashboardFromPaymentStep && !isManualEditMode" class="review-cards-grid" role="status" aria-live="polite">
            <section class="review-summary-card">
              <h5 class="review-summary-card__title mb-2">Seus dados foram revisados com sucesso</h5>
              <p class="review-summary-card__subtitle mb-3">Você já pode seguir para criar seu evento. Aqui está um resumo:</p>

              <div class="review-summary-card__row">
                <span class="review-summary-card__label">Dados pessoais</span>
                <span class="review-summary-card__value">{{ personalSummaryLine }}</span>
              </div>

              <div class="review-summary-card__row">
                <span class="review-summary-card__label">Endereco</span>
                <span class="review-summary-card__value">{{ addressSummaryLine }}</span>
              </div>

              <div class="review-summary-card__row review-summary-card__row--last">
                <span class="review-summary-card__label">Dados bancarios</span>
                <span class="review-summary-card__value">{{ bankSummaryLine }}</span>
              </div>
            </section>
          </div>

          <div v-else class="payment-embed-shell">
            <div v-if="showEmbeddedSkeleton" class="embedded-skeleton" aria-hidden="true">
              <div class="embedded-skeleton__line embedded-skeleton__line--title"></div>
              <div class="embedded-skeleton__line"></div>
              <div class="embedded-skeleton__line embedded-skeleton__line--short"></div>
              <div class="embedded-skeleton__card"></div>
              <div class="embedded-skeleton__card embedded-skeleton__card--small"></div>
            </div>

            <div ref="embeddedOnboardingHost" class="embedded-onboarding-host" :class="{ 'embedded-onboarding-host--ready': !showEmbeddedSkeleton }"></div>
          </div>
        </div>
      </div>
    </main>

    <!-- ── Footer navigation ─────────────────────────────────────── -->
    <footer class="app-footer">
      <div class="app-footer__inner">
        <p class="app-footer__step-hint">
          Passo {{ currentStep }} de {{ steps.length }} —
          <strong>{{ steps[currentStep - 1].name }}</strong>
        </p>

        <div class="app-footer__actions">
          <button
            class="btn btn-footer-back"
            @click="handleBack"
            :disabled="(currentStep === 1 && isCreateAccountFlow) || isSaving"
          >
            <i class="fas fa-chevron-left me-1"></i> {{ secondaryButtonLabel }}
          </button>

          <button
            class="btn btn-footer-finish"
            @click="handlePrimaryAction"
            :disabled="isPrimaryActionDisabled"
          >
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            {{ primaryButtonLabel }}
          </button>
        </div>
      </div>
    </footer>

    <!-- ── Modal de confirmação ──────────────────────────────────── -->
    <BottomModal
      :visible="showConfirmationModal"
      @close="showConfirmationModal = false"
      :fullHeight="false"
    >
      <template #default>
        <div class="confirmation-content p-3">
          <h5 class="text-center mb-3 text-primary-emphasis">Confirmar dados</h5>
          <p class="text-muted text-center mb-4 small">
            Revise as informações antes de salvar. Estes dados serão usados para seus recebimentos.
          </p>

          <!-- Dados pessoais -->
          <div class="summary-block mb-2">
            <p class="summary-block-title"><i class="fas fa-user me-2"></i>Dados Pessoais</p>
            <div class="summary-compact-list summary-compact-list--personal">
              <div class="summary-compact-item">
                <i class="fas fa-user summary-compact-icon"></i>
                <span class="summary-compact-value">{{ formData.full_name }}</span>
              </div>
              <div class="summary-compact-item">
                <i class="fas fa-envelope summary-compact-icon"></i>
                <span class="summary-compact-value">{{ formData.email }}</span>
              </div>
              <div class="summary-compact-item">
                <i class="fas fa-phone summary-compact-icon"></i>
                <span class="summary-compact-value">{{ formData.phone }}</span>
              </div>
              <div class="summary-compact-item summary-compact-item--last">
                <i class="fas fa-circle-check summary-compact-icon"></i>
                <span class="summary-compact-value">Dados prontos para uso na sua conta.</span>
              </div>
            </div>
          </div>

          <!-- Termo de aceite -->
          <div :class="['terms-acceptance', { 'terms-acceptance--error': termsError }]">
            <label class="terms-acceptance__card" for="termsCheckbox">
              <div class="terms-acceptance__checkbox-wrap">
                <input
                  type="checkbox"
                  id="termsCheckbox"
                  v-model="termsAccepted"
                  @change="termsError = false"
                />
                <span class="terms-acceptance__custom-check">
                  <i class="fas fa-check"></i>
                </span>
              </div>
              <span class="terms-acceptance__text">
                Li e concordo com os
                <a href="/termos-de-uso" target="_blank" class="terms-link" @click.stop>
                  Termos de Uso e Política de Privacidade
                </a>.
              </span>
            </label>
            <Transition name="terms-error">
              <div v-if="termsError" class="terms-acceptance__error">
                <i class="fas fa-exclamation-circle me-1"></i>Você precisa aceitar os termos para continuar.
              </div>
            </Transition>
          </div>

          <div class="confirmation-actions">
            <button class="btn btn-review" @click="showConfirmationModal = false" :disabled="isSaving">
              Revisar
            </button>
            <button class="btn btn-confirm-save" @click="confirmSave" :disabled="isSaving">
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              {{ isSaving ? 'Salvando...' : 'Confirmar e Salvar' }}
            </button>
          </div>
        </div>
      </template>
    </BottomModal>

    <!-- ── Modal de erro ─────────────────────────────────────────── -->
    <BottomModal
      :visible="showErrorModal"
      @close="showErrorModal = false"
      :fullHeight="false"
      :showCloseButton="true"
    >
      <template #default>
        <div class="p-3 text-center">
          <div class="mb-3" style="font-size: 2.5rem;">⚠️</div>
          <h5 class="mb-2 text-danger">Erro ao salvar dados</h5>
          <p class="text-muted mb-4">{{ errorMessage }}</p>
          <button class="btn btn-color-site" @click="showErrorModal = false">Fechar</button>
        </div>
      </template>
    </BottomModal>

  </div>
</template>

<script setup>
import { ref, computed, nextTick, onBeforeUnmount, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { toast } from 'vue3-toastify';
import { loadConnectAndInitialize } from '@stripe/connect-js/pure';
import { useAuthStore } from '@/stores/authStore';
import api from '@/services/api';
import FullScreenLoader from '@/components/FullScreenLoader.vue';
import BottomModal from '@/components/BottomModal.vue';
import ImageCropperModal from '@/components/ImageCropperModal.vue';
import Step1PersonalData from '@/components/BankDetailsSteps/Step1PersonalData.vue';
import {
  hasGatewayErrors,
  extractGatewayErrors,
  firstErrorStep,
  fieldErrorsToMap,
} from '@/utils/gatewayErrorHandler';

const authStore = useAuthStore();
const router = useRouter();
const route  = useRoute();
const stripePublishableKey = (process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
const isMobileDevice = ref(window.innerWidth <= 768);
const isCompactMobile = ref(window.innerWidth <= 430);
const isProfileReady = ref(false);

// ── Avatar ──────────────────────────────────────────────────
const avatarInputRef = ref(null);
const avatarCropperVisible = ref(false);
const avatarCropperSrc = ref('');
const avatarUploading = ref(false);
const avatarPreview = computed(() => authStore.user?.avatar_url || '');
const userInitial = computed(() => {
  const name = (authStore.user?.name || 'U').trim();
  return (name.charAt(0) || 'U').toUpperCase();
});

function triggerAvatarInput() {
  avatarInputRef.value?.click();
}

function onAvatarFileSelected(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  event.target.value = '';
  const reader = new FileReader();
  reader.onload = (e) => {
    avatarCropperSrc.value = e.target.result;
    avatarCropperVisible.value = true;
  };
  reader.readAsDataURL(file);
}

async function onAvatarCropConfirm({ blob }) {
  avatarCropperVisible.value = false;
  avatarUploading.value = true;
  try {
    const formData = new FormData();
    formData.append('avatar', blob, 'avatar.jpg');
    const response = await api.post('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const newUrl = response?.data?.avatar_url;
    if (newUrl) {
      authStore.user = { ...authStore.user, avatar_url: newUrl };
      toast.success('Foto atualizada!', { autoClose: 2500, position: 'top-right' });
    }
  } catch (err) {
    const message = err?.response?.data?.message || 'Erro ao salvar foto.';
    toast.error(message, { autoClose: 3500, position: 'top-right' });
  } finally {
    avatarUploading.value = false;
  }
}
// ── /Avatar ─────────────────────────────────────────────────
const embeddedOnboardingHost = ref(null);
const onboardingElement = ref(null);
const paymentStepLoading = ref(false);
const paymentStepError = ref('');
const embeddedInitializing = ref(false);
const embeddedContentReady = ref(false);
const canGoDashboardFromPaymentStep = ref(false);
const isManualEditMode = ref(false);

const showEmbeddedSkeleton = computed(() => {
  if (!isStripeFlow.value || currentStep.value !== 2) {
    return false;
  }

  if (paymentStepLoading.value || embeddedInitializing.value) {
    return true;
  }

  if (!onboardingElement.value) {
    return false;
  }

  return !embeddedContentReady.value;
});

const flowRef = computed(() => String(route.query.ref || '').trim().toLowerCase());
const isCreateAccountFlow = computed(() => flowRef.value === 'createaccount');
const isReviewAccountFlow = computed(() => flowRef.value === 'reviewaccount');
const isStripeFlow = computed(() => isCreateAccountFlow.value || isReviewAccountFlow.value);

// Mantido para compatibilidade com o restante do fluxo de onboarding inicial.
const isOnboarding = computed(() => isCreateAccountFlow.value);

onMounted(async () => {
  try {
    if (!authStore.profileLoaded && !authStore.profileLoading) {
      await authStore.fetchUserProfile();
    }
  } catch {
    // erro já tratado no store
  } finally {
    isProfileReady.value = true;
  }

  if (isCreateAccountFlow.value) {
    const raw = sessionStorage.getItem('register_data');
    if (raw) {
      try {
        const rd = JSON.parse(raw);
        const isComplete =
          rd.name?.trim().length >= 3 &&
          rd.email?.includes('@') &&
          rd.phone?.replace(/\D/g, '').length >= 10;

        if (isComplete) {
          Object.assign(formData.value, {
            full_name:    rd.name.trim(),
            email:        rd.email.trim(),
            phone:        rd.phone,
          });
        }
      } catch {
        // segue no Step1 normalmente
      }
    }
  }
});

// ── Step metadata ─────────────────────────────────────────────
const steps = computed(() => {
  if (isStripeFlow.value) {
    return [
      { id: 1, name: 'Dados Pessoais', icon: 'fas fa-user' },
      {
        id: 2,
        name: isReviewAccountFlow.value ? 'Revisar conta' : 'Revisar dados',
        icon: 'fas fa-credit-card',
      },
    ];
  }

  return [
    { id: 1, name: 'Dados Pessoais', icon: 'fas fa-user' },
  ];
});

const progressWidth = computed(() => {
  if (steps.value.length <= 1) return '100%';
  return `${((currentStep.value - 1) / (steps.value.length - 1)) * 100}%`;
});

// ── State ──────────────────────────────────────────────────
const currentStep = ref(1);
const isSaving    = ref(false);

const showConfirmationModal = ref(false);
const showErrorModal        = ref(false);
const errorMessage          = ref('');

const termsAccepted         = ref(false);
const termsError            = ref(false);
const gatewayFieldErrors    = ref({});

// Accumulated data from all steps
const formData = ref({
  full_name:    '',
  email:        '',
  phone:        '',
  birth_date:   '',
  cpf_document: '',
  postal_code:  '',
  address_number: '',
  address:      '',
  province:     '',
  city:         '',
  state:        '',
});

// ── Template refs (one per step component) ──────────────────────
const step1 = ref(null);
const stepRefs = [null, step1]; // index matches step.id

const primaryButtonLabel = computed(() => {
  if (isSaving.value) {
    return isStripeFlow.value
      ? (currentStep.value === 1 ? 'Preparando...' : 'Finalizando...')
      : 'Atualizando...';
  }

  if (!isStripeFlow.value) {
    return 'Atualizar dados';
  }

  if (currentStep.value === 1) {
    if (isReviewAccountFlow.value) {
      return 'Revisar sua conta';
    }

    return 'Revisar sua conta';
  }

  if (canGoDashboardFromPaymentStep.value) {
    if (isCompactMobile.value) {
      return isManualEditMode.value ? 'Cancelar' : 'Editar dados';
    }

    return isManualEditMode.value ? 'Cancelar edicao' : 'Editar meus dados';
  }

  return 'Concluir verificação';
});

const secondaryButtonLabel = computed(() => {
  if (currentStep.value === 2 && canGoDashboardFromPaymentStep.value) {
    if (isCompactMobile.value) {
      return 'Minha conta';
    }

    return 'Acessar minha conta';
  }

  return 'Voltar';
});

const isPrimaryActionDisabled = computed(() => {
  if (isSaving.value) {
    return true;
  }

  if (!isStripeFlow.value) {
    return false;
  }

  if (currentStep.value === 2 && !canGoDashboardFromPaymentStep.value) {
    return true;
  }

  return false;
});

const personalSummaryLine = computed(() => {
  const name = String(formData.value.full_name || authStore.user?.name || '').trim();
  const email = String(formData.value.email || '').trim();
  const phone = String(formData.value.phone || '').trim();

  const parts = [name, email, phone].filter(Boolean);
  if (parts.length === 0) {
    return 'Dados pessoais confirmados no parceiro Stripe.';
  }

  return parts.join(' • ');
});

const addressSummaryLine = computed(() => {
  const line = String(formData.value.address || '').trim();
  const number = String(formData.value.address_number || '').trim();
  const city = String(formData.value.city || '').trim();
  const state = String(formData.value.state || '').trim();

  const street = [line, number].filter(Boolean).join(', ');
  const location = [city, state].filter(Boolean).join(' - ');
  const summary = [street, location].filter(Boolean).join(' • ');

  return summary || 'Endereco validado no parceiro Stripe.';
});

const bankSummaryLine = computed(() => {
  const details = authStore.bankDetailsData || {};
  const bankName = String(details.bank_name || details.bankName || '').trim();
  const agency = String(details.agency || '').trim();
  const accountMasked = String(details.account_masked || details.accountMasked || '').trim();

  if (bankName || agency || accountMasked) {
    return [
      bankName || 'Banco cadastrado',
      agency ? `Ag: ${agency}` : '',
      accountMasked ? `Conta: ${accountMasked}` : '',
    ].filter(Boolean).join(' • ');
  }

  return 'Conta de recebimento validada no parceiro Stripe.';
});

// ── Navigation ────────────────────────────────────────────────
const handleBack = () => {
  if (currentStep.value === 2 && isManualEditMode.value) {
    isManualEditMode.value = false;
    clearEmbeddedOnboarding();
    return;
  }

  if (currentStep.value === 2 && canGoDashboardFromPaymentStep.value) {
    router.push('/my-account/dashboard');
    return;
  }

  if (currentStep.value > 1) {
    currentStep.value--;
    if (currentStep.value === 1 && step1.value && typeof step1.value.applyFormData === 'function') {
      step1.value.applyFormData(formData.value);
    }
  } else if (isMobileDevice.value) {
    router.back();
  }
};

const goToStep = (targetStep) => {
  if (!isStripeFlow.value) {
    return;
  }

  if (targetStep < currentStep.value) {
    currentStep.value = targetStep;
  } else if (targetStep > currentStep.value) {
    toast.info('Complete os passos anteriores primeiro.', {
      autoClose: 2500,
      position: 'top-right',
    });
  }
};

const clearGatewayError = (field) => {
  if (gatewayFieldErrors.value[field]) {
    const updated = { ...gatewayFieldErrors.value };
    updated[field] = '';
    gatewayFieldErrors.value = updated;
  }
};

const clearEmbeddedOnboarding = () => {
  if (!embeddedOnboardingHost.value || !onboardingElement.value) {
    embeddedContentReady.value = false;
    return;
  }

  if (embeddedOnboardingHost.value.contains(onboardingElement.value)) {
    embeddedOnboardingHost.value.removeChild(onboardingElement.value);
  }

  onboardingElement.value = null;
  embeddedContentReady.value = false;
};

const createStripeEmbeddedSurfaceTracker = () => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return {
      waitUntilSettled: async () => {},
      restore: () => {},
    };
  }

  const originalFetch = window.fetch.bind(window);
  let requestSeen = false;
  let pendingCount = 0;
  let settledResolver = null;
  let fallbackTimer = null;

  const settleIfReady = () => {
    if (!requestSeen || pendingCount > 0 || !settledResolver) {
      return;
    }

    const resolve = settledResolver;
    settledResolver = null;
    resolve();
  };

  window.fetch = async (...args) => {
    const input = args[0];
    const requestURL = typeof input === 'string'
      ? input
      : String(input?.url || '');

    const isEmbeddedSurfaceRequest = requestURL.includes('/v1/embedded_surfaces');

    if (isEmbeddedSurfaceRequest) {
      requestSeen = true;
      pendingCount += 1;
    }

    try {
      return await originalFetch(...args);
    } finally {
      if (isEmbeddedSurfaceRequest) {
        pendingCount = Math.max(0, pendingCount - 1);
        settleIfReady();
      }
    }
  };

  return {
    waitUntilSettled: () => new Promise((resolve) => {
      if (requestSeen && pendingCount === 0) {
        resolve();
        return;
      }

      settledResolver = resolve;
      fallbackTimer = setTimeout(() => {
        if (settledResolver) {
          const fallbackResolve = settledResolver;
          settledResolver = null;
          fallbackResolve();
        }
      }, 2200);
    }),
    restore: () => {
      window.fetch = originalFetch;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
      settledResolver = null;
      fallbackTimer = null;
    },
  };
};

const refreshOnboardingCompletionStatus = async () => {
  try {
    const response = await api.post('/users/finance/connected-accounts/stripe/activate-transfers');
    const payload = response?.data || {};

    authStore.applyBackendEligibilityFlags(payload);
    authStore.user = {
      ...(authStore.user || {}),
      ...(payload.provider_account_id ? { provider_account_id: payload.provider_account_id } : {}),
      ...(payload.provider_account_status ? { provider_account_status: payload.provider_account_status } : {}),
    };

    canGoDashboardFromPaymentStep.value = !payload.needs_onboarding;
    return !payload.needs_onboarding;
  } catch {
    return false;
  }
};

const waitForEmbeddedRender = async () => {
  const host = embeddedOnboardingHost.value;
  if (!host) return;

  const isRenderableStripeIframe = (iframe) => {
    if (!iframe) return false;

    const id = String(iframe.id || '').trim();
    const name = String(iframe.name || '').trim();
    const src = String(iframe.getAttribute('src') || '').trim();

    // Ignora iframe técnico da Stripe (data layer/controller), que é oculto e não é a UI.
    if (id === 'stripeDataLayerFrame' || name.startsWith('connectjs_controller')) {
      return false;
    }

    const style = window.getComputedStyle(iframe);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
      return false;
    }

    const rect = iframe.getBoundingClientRect();
    if (rect.width < 40 || rect.height < 120) {
      return false;
    }

    // Para segurança, aceita qualquer iframe visível, mas prioriza src da Stripe quando presente.
    if (src && src.includes('connect-js.stripe.com')) {
      return true;
    }

    return true;
  };

  const findRenderableIframe = () => {
    const candidates = host.querySelectorAll('iframe');
    for (const iframe of candidates) {
      if (isRenderableStripeIframe(iframe)) {
        return iframe;
      }
    }
    return null;
  };

  const waitForIframeLoad = (iframe) => new Promise((resolve) => {
    if (!iframe) {
      resolve();
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      iframe.removeEventListener('load', onLoad);
      clearTimeout(timeoutId);
      resolve();
    };

    const onLoad = () => finish();
    iframe.addEventListener('load', onLoad, { once: true });

    // Fallback para evitar loading infinito caso o evento não dispare.
    const timeoutId = setTimeout(() => {
      finish();
    }, 12000);
  });

  const waitForRenderableFramePaint = async () => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < 12000) {
      const iframe = findRenderableIframe();
      if (iframe) {
        await waitForIframeLoad(iframe);
        // Aguarda dois frames para o browser pintar o conteúdo visual do iframe.
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        return;
      }
      await new Promise((r) => setTimeout(r, 80));
    }
  };

  const existingRenderable = findRenderableIframe();
  if (existingRenderable) {
    await waitForIframeLoad(existingRenderable);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    return;
  }

  await new Promise((resolve) => {
    let resolved = false;

    const finish = () => {
      if (resolved) return;
      resolved = true;
      observer.disconnect();
      clearTimeout(timeoutId);
      resolve();
    };

    const observer = new MutationObserver(async () => {
      const iframe = findRenderableIframe();
      if (iframe) {
        observer.disconnect();
        await waitForIframeLoad(iframe);
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        finish();
      }
    });

    observer.observe(host, { childList: true, subtree: true });

    const timeoutId = setTimeout(() => {
      finish();
    }, 15000);
  });

  await waitForRenderableFramePaint();
};

const mountEmbeddedOnboarding = async (clientSecret) => {
  if (!stripePublishableKey) {
    throw new Error('VUE_APP_STRIPE_PUBLISHABLE_KEY não configurada.');
  }

  if (!embeddedOnboardingHost.value) {
    throw new Error('Container de onboarding não encontrado.');
  }

  embeddedInitializing.value = true;
  embeddedContentReady.value = false;
  clearEmbeddedOnboarding();

  const stripeSurfaceTracker = createStripeEmbeddedSurfaceTracker();
  const skeletonStartedAt = Date.now();

  const connectAppearance = {
    variables: {
      colorPrimary: '#FF4D94',
      colorBackground: '#ffffff',
      colorText: '#32355d',
      colorSecondaryText: '#6c757d',
      colorBorder: '#e8ebf2',
      actionPrimaryColorText: '#FF4D94',
      buttonPrimaryColorBackground: '#FF4D94',
      buttonPrimaryColorBorder: '#FF4D94',
      buttonPrimaryColorText: '#ffffff',
      borderRadius: '12px',
      formBorderRadius: '12px',
      buttonBorderRadius: '12px',
      overlayBorderRadius: '14px',
      fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, sans-serif',
    },
  };

  const connectInstance = loadConnectAndInitialize({
    publishableKey: stripePublishableKey,
    fetchClientSecret: async () => clientSecret,
    appearance: connectAppearance,
    fonts: [
      {
        cssSrc: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap',
      },
    ],
  });

  const accountOnboarding = connectInstance.create('account-onboarding');
  accountOnboarding.setCollectionOptions({
    fields: 'currently_due',
    futureRequirements: 'include',
  });

  accountOnboarding.setOnLoadError(() => {
    embeddedContentReady.value = false;
    embeddedInitializing.value = false;
  });

  accountOnboarding.setOnExit(async () => {
    const completed = await refreshOnboardingCompletionStatus();

    if (isManualEditMode.value) {
      isManualEditMode.value = false;
      clearEmbeddedOnboarding();
      if (!completed) {
        toast.info('Conclua os dados no painel Stripe para liberar o acesso ao dashboard.', {
          autoClose: 3500,
          position: 'top-right',
        });
      }
      return;
    }

    if (completed) {
      toast.success('Onboarding concluído! Você já pode ir para o dashboard.', {
        autoClose: 3000,
        position: 'top-right',
      });
      return;
    }

    toast.info('Conclua os dados no painel Stripe para liberar o acesso ao dashboard.', {
      autoClose: 3500,
      position: 'top-right',
    });
  });

  try {
    embeddedOnboardingHost.value.appendChild(accountOnboarding);
    onboardingElement.value = accountOnboarding;
    await waitForEmbeddedRender();
    await stripeSurfaceTracker.waitUntilSettled();

    // Evita o "flash branco": mantém skeleton por um tempo mínimo até o embed estabilizar visualmente.
    const minSkeletonMs = 1300;
    const elapsed = Date.now() - skeletonStartedAt;
    if (elapsed < minSkeletonMs) {
      await new Promise((resolve) => setTimeout(resolve, minSkeletonMs - elapsed));
    }

    await new Promise((resolve) => setTimeout(resolve, 160));
    embeddedContentReady.value = true;
    embeddedInitializing.value = false;
  } finally {
    stripeSurfaceTracker.restore();
  }
};

const goToPaymentStep = async () => {
  await new Promise((r) => setTimeout(r, 50));
  const result = await step1.value?.validateAndProceed();
  if (!result) return;

  if (!result.isValid) {
    if (result.message) {
      toast.error(result.message, { autoClose: 3000, position: 'top-right' });
    }
    return;
  }

  Object.assign(formData.value, result.data);

  isSaving.value = true;
  paymentStepLoading.value = true;
  paymentStepError.value = '';
  embeddedInitializing.value = false;
  canGoDashboardFromPaymentStep.value = false;

  try {
    const profilePayload = {
      name: formData.value.full_name,
      email: formData.value.email,
      phone: formData.value.phone,
      birth_date: formData.value.birth_date,
      cpf_document: formData.value.cpf_document,
      postal_code: formData.value.postal_code,
      address_number: formData.value.address_number,
      address: formData.value.address,
      province: formData.value.province,
      city: formData.value.city,
      state: formData.value.state,
    };

    await authStore.updateProfile(profilePayload);

    if (authStore.normalizedPlatformStatus === 'active') {
      currentStep.value = 2;
      await nextTick();
      embeddedInitializing.value = false;
      canGoDashboardFromPaymentStep.value = true;
      isManualEditMode.value = false;
      return;
    }

    const activatePayload = {
      postal_code: formData.value.postal_code,
      address_number: formData.value.address_number,
      address: formData.value.address,
      province: formData.value.province,
      city: formData.value.city,
      state: formData.value.state,
    };

    const response = await api.post('/users/finance/connected-accounts/stripe/activate-transfers', activatePayload);
    const payload = response?.data || {};

    authStore.applyBackendEligibilityFlags(payload);
    authStore.user = {
      ...(authStore.user || {}),
      ...(payload.provider_account_id ? { provider_account_id: payload.provider_account_id } : {}),
      ...(payload.provider_account_status ? { provider_account_status: payload.provider_account_status } : {}),
    };

    currentStep.value = 2;
    await nextTick();

    if (!payload.needs_onboarding) {
      embeddedInitializing.value = false;
      canGoDashboardFromPaymentStep.value = true;
      isManualEditMode.value = false;
      return;
    }

    const clientSecret = payload.account_session_client_secret;
    if (!clientSecret) {
      throw new Error('Sessão de onboarding embutido indisponível no momento.');
    }

    await mountEmbeddedOnboarding(clientSecret);
  } catch (err) {
    if (hasGatewayErrors(err)) {
      const fieldErrors = extractGatewayErrors(err);
      const targetStep = firstErrorStep(fieldErrors);

      currentStep.value = Math.min(Math.max(targetStep, 1), steps.value.length);
      gatewayFieldErrors.value = fieldErrorsToMap(fieldErrors);

      const firstError = fieldErrors[0];
      toast.error(firstError?.message || 'Verifique os dados e tente novamente.', {
        autoClose: 5000,
        position: 'top-right',
      });
      return;
    }

    const message = err?.response?.data?.message || err?.message || 'Ocorreu um erro inesperado. Tente novamente.';
    embeddedInitializing.value = false;
    paymentStepError.value = message;
    toast.error(message, { autoClose: 4000, position: 'top-right' });
  } finally {
    isSaving.value = false;
    paymentStepLoading.value = false;
  }
};

const saveProfileOnly = async () => {
  const result = await step1.value?.validateAndProceed();
  if (!result) return;

  if (!result.isValid) {
    if (result.message) {
      toast.error(result.message, { autoClose: 3000, position: 'top-right' });
    }
    return;
  }

  Object.assign(formData.value, result.data);

  isSaving.value = true;
  try {
    const profilePayload = {
      name: formData.value.full_name,
      email: formData.value.email,
      phone: formData.value.phone,
      birth_date: formData.value.birth_date,
      cpf_document: formData.value.cpf_document,
      postal_code: formData.value.postal_code,
      address_number: formData.value.address_number,
      address: formData.value.address,
      province: formData.value.province,
      city: formData.value.city,
      state: formData.value.state,
    };

    await authStore.updateProfile(profilePayload);
    toast.success('Dados atualizados com sucesso!', { autoClose: 3000, position: 'top-right' });
  } catch (err) {
    if (hasGatewayErrors(err)) {
      const fieldErrors = extractGatewayErrors(err);
      gatewayFieldErrors.value = fieldErrorsToMap(fieldErrors);

      const firstError = fieldErrors[0];
      toast.error(firstError?.message || 'Verifique os dados e tente novamente.', {
        autoClose: 5000,
        position: 'top-right',
      });
      return;
    }

    const message = err?.response?.data?.message || err?.message || 'Ocorreu um erro inesperado. Tente novamente.';
    toast.error(message, { autoClose: 4000, position: 'top-right' });
  } finally {
    isSaving.value = false;
  }
};

const confirmSave = async () => {
  // Validar aceite dos termos
  if (!termsAccepted.value) {
    termsError.value = true;
    return;
  }
  termsError.value = false;

  isSaving.value = true;
  try {
    // 1. Atualiza perfil (dados pessoais)
    const profilePayload = {
      name:           formData.value.full_name,
      email:          formData.value.email,
      phone:          formData.value.phone,
      terms_accepted: true,
    };
    await authStore.updateProfile(profilePayload);

    showConfirmationModal.value = false;
    toast.success('Dados salvos com sucesso! 🎉', { autoClose: 3000, position: 'top-right' });
  sessionStorage.removeItem('register_data');

    // Redireciona para dashboard com celebração após onboarding
    if (isOnboarding.value) {
      authStore.setOnboardingMode(false);
      router.push('/my-account/dashboard?onboarding=complete');
    }
  } catch (err) {
    showConfirmationModal.value = false;

    if (hasGatewayErrors(err)) {
      const fieldErrors = extractGatewayErrors(err);
      const targetStep = firstErrorStep(fieldErrors);

      currentStep.value = Math.min(Math.max(targetStep, 1), steps.value.length);
      gatewayFieldErrors.value = fieldErrorsToMap(fieldErrors);

      const firstError = fieldErrors[0];
      toast.error(firstError?.message || 'Verifique os dados e tente novamente.', {
        autoClose: 5000,
        position: 'top-right',
      });
      return;
    }

    const status = err?.response?.status;
    const message = err?.response?.data?.message || 'Ocorreu um erro inesperado. Tente novamente.';

    if (status === 401) {
      toast.error(message, { autoClose: 4000, position: 'top-right' });
    } else {
      errorMessage.value = message;
      showErrorModal.value = true;
    }
  } finally {
    isSaving.value = false;
  }
};

const handlePrimaryAction = async () => {
  if (!isStripeFlow.value) {
    await saveProfileOnly();
    return;
  }

  if (currentStep.value === 1) {
    await goToPaymentStep();
    return;
  }

  if (!canGoDashboardFromPaymentStep.value) {
    toast.info('Finalize o cadastro dentro do painel Stripe para continuar.', {
      autoClose: 3000,
      position: 'top-right',
    });
    return;
  }

  if (isManualEditMode.value) {
    isManualEditMode.value = false;
    clearEmbeddedOnboarding();
    return;
  }

  paymentStepLoading.value = true;
  paymentStepError.value = '';
  try {
    const response = await api.post('/users/finance/connected-accounts/stripe/bank-update-session');
    const clientSecret = String(response?.data?.account_session_client_secret || '').trim();
    if (!clientSecret) {
      throw new Error('Sessao embutida indisponivel no momento.');
    }

    isManualEditMode.value = true;
    await nextTick();
    await mountEmbeddedOnboarding(clientSecret);
  } catch (err) {
    isManualEditMode.value = false;
    const message = err?.response?.data?.message || err?.message || 'Nao foi possivel iniciar a edicao agora.';
    paymentStepError.value = message;
    toast.error(message, { autoClose: 4000, position: 'top-right' });
  } finally {
    paymentStepLoading.value = false;
  }
};

onBeforeUnmount(() => {
  embeddedInitializing.value = false;
  clearEmbeddedOnboarding();
});

if (typeof window !== 'undefined') {
  const handleResize = () => {
    isMobileDevice.value = window.innerWidth <= 768;
    isCompactMobile.value = window.innerWidth <= 430;
  };

  window.addEventListener('resize', handleResize);
  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
  });
}

const paymentStepTitle = computed(() => {
  if (isReviewAccountFlow.value) {
    return 'Revise sua conta de recebimento';
  }

  if (isCreateAccountFlow.value) {
    return 'Revise sua conta de recebimento';
  }

  return 'Dados de Pagamento';
});

const paymentStepDescription = computed(() => {
  if (isReviewAccountFlow.value) {
    return 'Atualize os dados necessários para reativar seus recebimentos.';
  }

  if (isCreateAccountFlow.value) {
    return 'Agora, revise seus dados para liberar seus recebimentos.';
  }

  return 'Agora, confirme seus dados para ativar seus recebimentos.';
});

</script>

<style scoped>
.create-event-page {
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  min-height: 100%;
  width: 100%;
  min-width: 600px;
  overflow: visible;
}

/* ── Onboarding header banner ─────────────────────── */
.onboarding-header {
  background: linear-gradient(135deg, #fff0f8, #fdf2f8);
  border-bottom: 1px solid #ffd6eb;
  padding: 0.875rem 1.5rem;
  text-align: center;
}
.onboarding-header__progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
  flex-wrap: wrap;
}
.onboarding-header__step {
  font-size: 0.78rem;
  font-weight: 600;
  color: #adb5bd;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}
.onboarding-header__step--done   { color: #ff50a3; }
.onboarding-header__step--active { color: #32355d; }
.onboarding-header__arrow        { color: #ced4da; font-size: 0.8rem; }
.onboarding-header__hint {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 0;
}

.step-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #32355d;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.step-description {
  font-size: 0.92rem;
  color: #6c757d;
  margin-bottom: 0.55rem;
}

.payment-stage-header {
  background: #ffffff;
  border: 1px solid #e9edf5;
  border-radius: 0.9rem;
  padding: 0.92rem 1rem 0.78rem;
}

.payment-stage-header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  margin-bottom: 0.28rem;
}

.title-verified-seal {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  color: #ffffff;
  background: #2f80ed;
  box-shadow: 0 0 0 2px rgba(47, 128, 237, 0.16);
}

.payment-stage-security {
  display: inline-flex;
  align-items: center;
  font-size: 0.76rem;
  font-weight: 600;
  color: #6b7285;
  background: #f7f9fc;
  border: 1px solid #e6ebf4;
  border-radius: 999px;
  padding: 0.2rem 0.56rem;
  margin-left: auto;
  white-space: nowrap;
}

.payment-stage-benefits {
  display: flex;
  gap: 0.42rem;
  font-size: 0.74rem;
  color: #5a647f;
}

.payment-stage-benefits--compact {
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
}

.payment-stage-benefits--compact::-webkit-scrollbar {
  display: none;
}

.payment-stage-benefits span {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  background: #f8faff;
  border: 1px solid #e8edf6;
  border-radius: 999px;
  padding: 0.16rem 0.5rem;
}

.payment-embed-shell {
  border: none;
  border-radius: 0;
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.review-cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.78rem;
}


.review-summary-card {
  border: 1px solid #e6ebf4;
  border-radius: 14px;
  background: #ffffff;
  padding: 0.95rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
}

.review-summary-card__title {
  font-size: 1rem;
  font-weight: 700;
  color: #2f3456;
}

.review-summary-card__subtitle {
  font-size: 0.86rem;
  color: #6c7389;
}

.review-summary-card__row {
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
  padding: 0.48rem 0;
  border-bottom: 1px dashed #e5eaf4;
  min-width: 0;
}

.review-summary-card__row--last {
  border-bottom: none;
  padding-bottom: 0;
}

.review-summary-card__label {
  font-size: 0.76rem;
  font-weight: 700;
  color: #6a728a;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.review-summary-card__value {
  font-size: 0.88rem;
  font-weight: 600;
  color: #3b4462;
  line-height: 1.35;
  display: block;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.bank-summary-line {
  color: #384262;
  font-weight: 700;
  font-size: 0.91rem;
  line-height: 1.35;
}

.bank-summary-meta {
  font-size: 0.84rem;
  color: #6b738c;
  line-height: 1.35;
}

.embedded-onboarding-host {
  width: 100%;
  min-height: 5px;
  background: #ffffff;
  border: 1px solid #f0f2f7;
  border-radius: 14px;
  box-shadow: 0 8px 22px rgba(50, 53, 93, 0.05);
  padding: 0.85rem;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.embedded-onboarding-host--ready {
  opacity: 1;
  transform: translateY(0);
}

.embedded-onboarding-host > * {
  display: block;
  width: 100%;
  min-height: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.embedded-skeleton {
  border-radius: 0.8rem;
  padding: 0.95rem;
  margin-bottom: 0.55rem;
  border: none;
  background: linear-gradient(180deg, #ffffff, #fafbff);
}

.embedded-skeleton__line,
.embedded-skeleton__card {
  position: relative;
  overflow: hidden;
  background: #eceff3;
  border-radius: 0.5rem;
}

.embedded-skeleton__line::after,
.embedded-skeleton__card::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.65), transparent);
  animation: embedded-shimmer 1.3s infinite;
}

.embedded-skeleton__line {
  height: 12px;
  margin-bottom: 0.55rem;
}

.embedded-skeleton__line--title {
  width: 50%;
  height: 16px;
}

.embedded-skeleton__line--short {
  width: 65%;
}

.embedded-skeleton__card {
  height: 180px;
  margin-top: 0.8rem;
}

.embedded-skeleton__card--small {
  height: 58px;
  margin-top: 0.7rem;
}

@keyframes embedded-shimmer {
  100% {
    transform: translateX(100%);
  }
}

.terms-link {
  color: #ff50a3;
  font-weight: 600;
  text-decoration: underline;
  white-space: nowrap;
}
.terms-link:hover {
  color: #e0408e;
}

/* ── Terms acceptance card ───────────────────────── */
.terms-acceptance {
  margin: 1rem 0;
}
.terms-acceptance__card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1.5px solid #e9ecef;
  border-radius: 0.75rem;
  background: #fafafa;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  user-select: none;
  margin: 0;
}
.terms-acceptance__card:hover {
  border-color: #ffb3d9;
  background: #fff8fb;
}
.terms-acceptance--error .terms-acceptance__card {
  border-color: #dc3545;
  background: #fff5f5;
}

/* Hide native checkbox */
.terms-acceptance__checkbox-wrap {
  position: relative;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin-top: 1px;
}
.terms-acceptance__checkbox-wrap input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  margin: 0;
  z-index: 1;
}
.terms-acceptance__custom-check {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 2px solid #ced4da;
  border-radius: 6px;
  background: #fff;
  transition: all 0.18s ease;
  pointer-events: none;
}
.terms-acceptance__custom-check .fa-check {
  font-size: 0.65rem;
  color: transparent;
  transition: color 0.15s ease;
}
.terms-acceptance__checkbox-wrap input:checked + .terms-acceptance__custom-check {
  background-color: #ff50a3;
  border-color: #ff50a3;
  box-shadow: 0 0 0 3px rgba(255, 80, 163, 0.15);
}
.terms-acceptance__checkbox-wrap input:checked + .terms-acceptance__custom-check .fa-check {
  color: #fff;
}
.terms-acceptance--error .terms-acceptance__custom-check {
  border-color: #dc3545;
}

.terms-acceptance__text {
  font-size: 0.82rem;
  color: #495057;
  line-height: 1.45;
  padding-top: 1px;
}
.terms-acceptance__error {
  font-size: 0.79rem;
  color: #dc3545;
  margin-top: 0.4rem;
  padding-left: 0.25rem;
  display: flex;
  align-items: center;
}

/* Transition */
.terms-error-enter-active,
.terms-error-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.terms-error-enter-from,
.terms-error-leave-to { opacity: 0; transform: translateY(-4px); }

.pointer-events-none { pointer-events: none; }

.steps-navigation {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 90;
  background-color: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding: 1rem 1.5rem 0.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  margin-top: 0;
}

.progress-track {
  position: relative;
  height: 3px;
  background-color: #e9ecef;
  border-radius: 99px;
  margin: 0 auto 1rem;
  max-width: 320px;
}

.progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #ff50a3, #ff8cc8);
  border-radius: 99px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.steps-row {
  display: flex;
  justify-content: space-around;
  max-width: 360px;
  margin: 0 auto;
  gap: 8px;
}

.step-dot-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  gap: 5px;
  opacity: 0.45;
  transition: opacity 0.2s;
  min-width: 0;
  flex: 1;
}

.step-dot-item.active,
.step-dot-item.completed {
  opacity: 1;
}

.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #9e9e9e;
  transition: all 0.3s ease;
}

.step-dot-item.active .step-dot {
  background-color: #ff50a3;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(255, 80, 163, 0.18);
}

.step-dot-item.completed .step-dot {
  background-color: #32355d;
  color: #fff;
}

.step-dot-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #9e9e9e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.step-dot-item.active .step-dot-label {
  color: #ff50a3;
}

.step-dot-item.completed .step-dot-label {
  color: #32355d;
}

.step-content-wrapper {
  flex-grow: 1;
  padding: 0px !important;
  max-width: 640px;
  margin: 0 auto;
  width: 100%;
  min-width: 0;
  overflow: visible;
  box-sizing: border-box;
}

.step-content-wrapper > .container-fluid {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  overflow-x: clip;
  box-sizing: border-box;
}

.app-footer {
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 100;
  background: transparent;
  pointer-events: none;
}

.app-footer__inner {
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.07);
  padding: 0.75rem 1.25rem 1rem;
  pointer-events: all;
  max-width: 640px;
  margin: 0 auto;
  border-radius: 16px 16px 0 0;
}

.app-footer__step-hint {
  font-size: 0.72rem;
  color: #b0b0b0;
  text-align: center;
  margin-bottom: 0.6rem;
}

.app-footer__actions {
  display: flex;
  gap: 10px;
}

.btn-footer-back {
  background: #fff;
  border: 1.5px solid #e0e0e0;
  color: #6c757d;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.88rem;
  font-weight: 600;
  transition: all 0.18s;
  flex-shrink: 0;
}

.btn-footer-back:hover:not(:disabled) {
  border-color: #adb5bd;
  color: #32355d;
}

.btn-footer-back:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.btn-footer-finish {
  flex: 1;
  background: #ff50a3 !important;
  background-image: linear-gradient(135deg, #ff50a3, #d63384) !important;
  color: #fff !important;
  border: none;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.92rem;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(255, 80, 163, 0.35);
  transition: opacity 0.18s, transform 0.15s;
}

.btn-footer-finish:hover {
  background: #e8417f !important;
  background-image: linear-gradient(135deg, #e8417f, #c0296e) !important;
  color: #fff !important;
  opacity: 1;
  transform: translateY(-1px);
}

.btn-footer-finish:focus,
.btn-footer-finish:active,
.btn-footer-finish:focus-visible {
  background: #e8417f !important;
  background-image: linear-gradient(135deg, #e8417f, #c0286e) !important;
  color: #fff !important;
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(255, 80, 163, 0.35);
}

.btn-color-site {
  background-color: #ff50a3;
  color: white;
  border: 1px solid #ff50a3;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}
.btn-color-site:hover {
  background-color: #e6408f;
  border-color: #e6408f;
  color: white;
}
.btn-color-site:active {
  background-color: #d1307b !important;
  border-color: #d1307b !important;
  color: white !important;
}
.btn-color-site:focus {
  box-shadow: 0 0 0 0.25rem rgba(255, 80, 163, 0.25);
}

.btn-outline-secondary {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #6c757d;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}
.btn-outline-secondary:hover {
  background-color: #6c757d;
  color: white;
  border-color: #6c757d;
}
.btn-outline-secondary:active {
  background-color: #5a6268 !important;
  border-color: #5a6268 !important;
  color: white !important;
}
.btn-outline-secondary:focus {
  box-shadow: 0 0 0 0.25rem rgba(108, 117, 125, 0.25);
}

/* Confirmation modal */
.confirmation-content {
  max-height: 68vh;
  overflow-y: auto;
}

.summary-block-title {
  font-weight: 700;
  color: #32355d;
  margin-bottom: 0.4rem;
  font-size: 0.86rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.summary-compact-list {
  border-radius: 0.75rem;
  padding: 0.45rem 0.7rem;
  border: 1px solid #eceef3;
}

.summary-compact-list--personal {
  background: linear-gradient(180deg, #fff7fc 0%, #fff 100%);
  border-left: 3px solid #ff50a3;
}

.summary-compact-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.25rem 0;
  border-bottom: 1px dashed #e9ecf2;
}

.summary-compact-item--last {
  border-bottom: none;
}

.summary-compact-icon {
  color: #8b94a7;
  font-size: 0.78rem;
  margin-top: 0.22rem;
  width: 14px;
  text-align: center;
  flex-shrink: 0;
}

.summary-compact-value {
  font-size: 0.9rem;
  color: #2f3453;
  font-weight: 500;
  line-height: 1.28;
}

.confirmation-actions {
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
}

.btn-review {
  flex: 0 0 auto;
  min-width: 124px;
  background: #fff;
  color: #6b7280;
  border: 1.5px solid #d8dbe3;
  border-radius: 0.65rem;
  font-weight: 600;
  padding: 0.7rem 1rem;
}

.btn-review:hover:not(:disabled) {
  border-color: #b8beca;
  color: #4b5563;
}

.btn-confirm-save {
  flex: 1 1 auto;
  min-width: 0;
  background: linear-gradient(135deg, #ff50a3 0%, #e0185d 100%);
  color: #fff;
  border: none;
  border-radius: 0.65rem;
  font-weight: 700;
  padding: 0.7rem 1rem;
  white-space: nowrap;
  line-height: 1.2;
}

.btn-confirm-save:hover:not(:disabled) {
  opacity: 0.92;
}

.btn-confirm-save:disabled,
.btn-review:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
@media (max-width: 767.98px) {
  .create-event-page {
    transform: translateZ(0);
    min-width: 0;
  }

  .steps-navigation {
    position: -webkit-sticky;
    position: sticky;
    top: -1rem;
    z-index: 200;
    margin-top: -1rem;
    padding: 0.75rem 1rem 0.65rem;
  }

    .payment-stage-header {
      padding: 0.84rem 0.82rem 0.72rem;
    }

    .payment-stage-security {
      font-size: 0.72rem;
      padding: 0.18rem 0.48rem;
    }

    .payment-stage-benefits {
      display: none;
    }

    .payment-stage-benefits span {
      font-size: 0.71rem;
      padding: 0.14rem 0.44rem;
    }

    .review-cards-grid {
      gap: 0.62rem;
    }

    .review-summary-card {
      padding: 0.78rem;
      border-radius: 12px;
    }

    .review-summary-card__title {
      font-size: 0.92rem;
    }

    .review-summary-card__subtitle {
      font-size: 0.8rem;
    }

    .review-summary-card__label {
      font-size: 0.7rem;
    }

    .review-summary-card__value {
      font-size: 0.82rem;
    }

    .step-dot {
      width: 26px;
      height: 26px;
    }

    .step-dot-label {
      font-size: 0.68rem;
  }

    .confirmation-actions {
      gap: 0.6rem;
    }

    .confirmation-content {
      max-height: none;
      overflow: visible;
    }

    .summary-compact-value {
      font-size: 0.86rem;
    }

    .summary-compact-list--personal {
      overflow-y: auto;
      overscroll-behavior: contain;
      scrollbar-gutter: stable;
    }

    .summary-compact-list--personal {
      max-height: 132px;
    }

    .summary-compact-list--personal::-webkit-scrollbar {
      width: 5px;
    }

    .onboarding-header{
      margin-top: 1rem;
    }

    .summary-compact-list--personal::-webkit-scrollbar-thumb {
      background: #cfd4df;
      border-radius: 99px;
    }

    .btn-review {
      min-width: 110px;
      padding-left: 0.85rem;
      padding-right: 0.85rem;
    }

    .btn-confirm-save {
      font-size: 0.92rem;
    }

  .app-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1031;
  }

    .app-footer__inner {
      max-width: 100%;
      border-radius: 12px 12px 0 0;
        padding-left: 0.5rem;
        padding-right: 0.5rem;
    }

      .app-footer__actions {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 8px;
        align-items: stretch;
      }

    .btn-footer-back,
    .btn-footer-finish {
        min-width: 0;
        width: 100%;
      padding: 0.52rem 0.5rem;
      font-size: 0.74rem;
      line-height: 1.15;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

  .step-content-wrapper {
      padding: 1.5rem 1rem 5rem !important;
  }

}

/* ── Avatar section ─────────────────────────────────────── */
.avatar-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0 1.25rem;
  border-bottom: 1px solid var(--border-line-color, #ececec);
  margin-bottom: 1.25rem;
}

.avatar-preview {
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  overflow: hidden;
}

.avatar-preview__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.avatar-preview__placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: var(--primary-color, #f78da7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-preview__initial {
  color: #fff;
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1;
}

.avatar-preview__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  color: #fff;
  font-size: 1.1rem;
}

.avatar-preview:hover .avatar-preview__overlay {
  opacity: 1;
}

.avatar-section__info {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.avatar-section__name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--secondary-color, #32355d);
  margin: 0;
}

.avatar-section__btn {
  background: none;
  border: 1px solid var(--primary-color, #f78da7);
  color: var(--primary-color, #f78da7);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  width: fit-content;
}

.avatar-section__btn:hover:not(:disabled) {
  background: var(--primary-color, #f78da7);
  color: #fff;
}

.avatar-section__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>